import { getToApi, postToApi, deleteToApi } from './axios';

// API wrappers para adjuntos de certificaciones MINVU (certificacion_attachment).
// Patrón calcado de api/coleccionItemImage.js: presigned PUT directo a S3 para
// subir, confirm persiste la fila, url devuelve presigned GET, delete limpia
// S3 + fila.

/**
 * @typedef {Object} CertificacionAttachment
 * @property {number} id
 * @property {number} id_certificacion
 * @property {string} s3_key
 * @property {string} filename
 * @property {string} content_type
 * @property {number|null} size_bytes
 * @property {string} createdAt
 */

/**
 * Lista los adjuntos de una certificación.
 * @param {number|string} certId
 * @returns {Promise<CertificacionAttachment[]>}
 */
export const listCertificacionAttachments = async (certId) => {
  const response = await getToApi(`/certificaciones/${certId}/attachments`);
  // getToApi devuelve el body ya deserializado.
  return Array.isArray(response) ? response : response?.data ?? [];
};

/**
 * Pide al backend una URL presigned PUT para subir un adjunto a S3.
 * @param {number|string} certId
 * @param {'application/pdf'|'image/jpeg'|'image/png'} contentType
 * @param {string} filename
 * @returns {Promise<{ uploadUrl: string, key: string, expiresAt: string }>}
 */
export const presignCertificacionAttachmentUpload = async (certId, contentType, filename) => {
  const response = await postToApi(`/certificaciones/${certId}/attachments/presign-upload`, {
    contentType,
    filename,
  });
  const data = response.data || response;
  if (data.state !== 'success') {
    throw new Error(data.message || 'Error generando presigned URL');
  }
  return {
    uploadUrl: data.uploadUrl,
    key: data.key,
    expiresAt: data.expiresAt,
  };
};

/**
 * Confirma el upload: persiste la fila en certificacion_attachment.
 * @param {number|string} certId
 * @param {{ key: string, filename: string, contentType: string, sizeBytes?: number|null }} payload
 * @returns {Promise<CertificacionAttachment>}
 */
export const confirmCertificacionAttachment = async (certId, payload) => {
  const response = await postToApi(`/certificaciones/${certId}/attachments/confirm`, payload);
  const data = response.data || response;
  if (data.state !== 'success') {
    throw new Error(data.message || 'Error confirmando el adjunto');
  }
  return data.attachment;
};

/**
 * Obtiene una URL presigned GET (1h) para ver/descargar un adjunto.
 * @param {number|string} certId
 * @param {number|string} attId
 * @returns {Promise<{ url: string, filename: string, content_type: string, expiresAt: string }>}
 */
export const getCertificacionAttachmentUrl = async (certId, attId) => {
  const data = await getToApi(`/certificaciones/${certId}/attachments/${attId}/url`);
  if (data.state !== 'success') {
    throw new Error(data.message || 'Error obteniendo URL del adjunto');
  }
  return {
    url: data.url,
    filename: data.filename,
    content_type: data.content_type,
    expiresAt: data.expiresAt,
  };
};

/**
 * Borra un adjunto (S3 + fila). Idempotente.
 * @param {number|string} certId
 * @param {number|string} attId
 * @returns {Promise<void>}
 */
export const deleteCertificacionAttachment = async (certId, attId) => {
  await deleteToApi(`/certificaciones/${certId}/attachments/${attId}`);
};
